import { ChangeEvent, DragEvent, MouseEvent, useCallback, useEffect, useRef, useState } from "react";
import { keyframes, styled } from "@mui/material";
import { Field } from "react-final-form";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { ClickableContainer } from "../../../utils/table-schemas";

type DocUploadProps = {
  onUpload?(): void;
  heading?: string;
  subheading?: string;
  title?: string;
  setFile?(file: File | undefined): void;
  style?: any;
  isFileDeleted?: boolean;
  acceptImages?: boolean;
  acceptOnlyImages?: boolean;
  disabled?: boolean;
  file?: File | any;
  uploadButton?: React.ReactNode;
  checkbox?: boolean;
  component?: React.ComponentType<any>;
  form?: any;
  label?: string;
};

type UploadStatus = "idle" | "dragging" | "uploading" | "paused" | "cancelled" | "completed";

const MAX_FILE_SIZE = 100_000_000;

const truncate = (name: string): string => (name.length > 30 ? `${name.slice(0, 28)}…` : name);

const DocUploadNew = ({
  uploadButton,
  file,
  title,
  heading,
  subheading,
  component,
  form,
  label,
  setFile,
  checkbox,
  style,
  isFileDeleted,
  acceptImages,
  acceptOnlyImages,
  disabled,
}: DocUploadProps) => {
  const [selectedFile, setSelectedFile] = useState<File | any>(file);
  const [error, setError] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string | undefined>();

  // Upload simulator state — mirrors SignerCard's UploadDropZone behaviour.
  const [uploadStatus, setUploadStatusRaw] = useState<UploadStatus>(file ? "completed" : "idle");
  const [progress, setProgress] = useState(file ? 100 : 0);
  const [uploadFilename, setUploadFilename] = useState(file?.name || "");
  const [speed, setSpeed] = useState("— MB/s");
  const [eta, setEta] = useState("Calculating…");

  const statusRef = useRef<UploadStatus>(file ? "completed" : "idle");
  const progressRef = useRef(file ? 100 : 0);
  const pendingFileRef = useRef<File | null>(null);
  const intervalRef = useRef<number | null>(null);
  const uploadRef = useRef<HTMLInputElement>(null);

  const setUploadStatus = useCallback((next: UploadStatus) => {
    statusRef.current = next;
    setUploadStatusRaw(next);
  }, []);

  const updateProgress = useCallback((next: number) => {
    progressRef.current = next;
    setProgress(next);
  }, []);

  const clearTick = useCallback(() => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Sync external `file` prop changes (drafts) — treat as already completed.
  useEffect(() => {
    if (file) {
      setSelectedFile(file);
      setUploadFilename(file.name || "Uploaded file");
      setUploadStatus("completed");
      updateProgress(100);
    }
  }, [file, setUploadStatus, updateProgress]);

  // Thumbnail preview for image files / server objects with a `url`.
  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl(undefined);
      return undefined;
    }
    if (selectedFile instanceof File && selectedFile.type.startsWith("image/")) {
      const objectUrl = URL.createObjectURL(selectedFile);
      setPreviewUrl(objectUrl);
      return () => {
        URL.revokeObjectURL(objectUrl);
        setPreviewUrl(undefined);
      };
    }
    if (typeof selectedFile === "object" && selectedFile.url) {
      setPreviewUrl(selectedFile.url);
      return undefined;
    }
    setPreviewUrl(undefined);
    return undefined;
  }, [selectedFile]);

  const resetState = useCallback(() => {
    clearTick();
    pendingFileRef.current = null;
    setUploadFilename("");
    setSpeed("— MB/s");
    setEta("Calculating…");
    updateProgress(0);
    setUploadStatus("idle");
    setSelectedFile(undefined);
    setError("");
    if (uploadRef.current) {
      // @ts-ignore
      uploadRef.current.value = null;
    }
    if (setFile) {
      setFile(undefined);
    }
  }, [clearTick, setFile, setUploadStatus, updateProgress]);

  useEffect(() => {
    if (isFileDeleted && selectedFile) resetState();
  }, [isFileDeleted, selectedFile, resetState]);

  const completeUpload = useCallback(() => {
    const f = pendingFileRef.current;
    if (!f) return;
    setUploadStatus("completed");
    setSelectedFile(f);
    if (setFile) setFile(f);
  }, [setFile, setUploadStatus]);

  const finishUpload = useCallback(() => {
    clearTick();
    updateProgress(100);
    setSpeed("");
    setEta("");
    completeUpload();
  }, [clearTick, completeUpload, updateProgress]);

  const runTicker = useCallback(() => {
    clearTick();
    intervalRef.current = window.setInterval(() => {
      if (statusRef.current !== "uploading") return;
      const next = Math.min(progressRef.current + Math.random() * 6 + 2, 100);
      updateProgress(next);
      const spd = (Math.random() * 3 + 1.5).toFixed(1);
      const remaining = Math.round((100 - next) / 8);
      setSpeed(`${spd} MB/s`);
      setEta(remaining > 0 ? `ETA ${remaining}s` : "Almost…");
      if (next >= 100) {
        clearTick();
        window.setTimeout(finishUpload, 300);
      }
    }, 80);
  }, [clearTick, finishUpload, updateProgress]);

  const startUpload = useCallback(
    (f: File) => {
      if (statusRef.current === "uploading" || statusRef.current === "paused") return;
      pendingFileRef.current = f;
      setUploadFilename(truncate(f.name));
      updateProgress(0);
      setSpeed("— MB/s");
      setEta("Calculating…");
      setError("");
      setUploadStatus("uploading");
      runTicker();
    },
    [runTicker, setUploadStatus, updateProgress]
  );

  useEffect(() => () => clearTick(), [clearTick]);

  const acceptUpload = useCallback(
    (f: File) => {
      if (f.size > MAX_FILE_SIZE) {
        setError(`Selected file is too big! Size should not exceed ${MAX_FILE_SIZE / 1_000_000} MB`);
        return;
      }
      startUpload(f);
    },
    [startUpload]
  );

  const handleBrowseClick = () => {
    if (disabled) return;
    uploadRef.current?.click();
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) acceptUpload(f);
    e.target.value = "";
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (disabled) return;
    if (statusRef.current === "idle" || statusRef.current === "dragging") {
      setUploadStatus("dragging");
    }
  };

  const handleDragLeave = () => {
    if (statusRef.current === "dragging") setUploadStatus("idle");
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (disabled) return;
    if (statusRef.current === "dragging") setUploadStatus("idle");
    if (statusRef.current === "uploading" || statusRef.current === "paused") return;
    const f = e.dataTransfer.files?.[0];
    if (f) acceptUpload(f);
  };

  const handleZoneClick = () => {
    if (disabled) return;
    if (statusRef.current === "idle" || statusRef.current === "dragging") handleBrowseClick();
  };

  const togglePause = (e: MouseEvent) => {
    e.stopPropagation();
    if (statusRef.current === "uploading") {
      setUploadStatus("paused");
      setSpeed("— MB/s");
      setEta("Paused");
    } else if (statusRef.current === "paused") {
      setUploadStatus("uploading");
    }
  };

  const cancelUpload = (e: MouseEvent) => {
    e.stopPropagation();
    clearTick();
    setUploadStatus("cancelled");
    setSpeed("");
    setEta("");
  };

  const retryUpload = (e: MouseEvent) => {
    e.stopPropagation();
    const f = pendingFileRef.current;
    setError("");
    if (f) {
      setUploadStatus("idle");
      startUpload(f);
    }
  };

  const dismissCancel = (e: MouseEvent) => {
    e.stopPropagation();
    resetState();
  };

  const replaceCompleted = (e: MouseEvent) => {
    e.stopPropagation();
    resetState();
    handleBrowseClick();
  };

  const removeCompleted = (e: MouseEvent) => {
    e.stopPropagation();
    resetState();
  };

  const openFile = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      if (!selectedFile) return;
      let urlToOpen: string | undefined;
      let revokeAfter = false;
      if (selectedFile instanceof File) {
        urlToOpen = URL.createObjectURL(selectedFile);
        revokeAfter = true;
      } else if (typeof selectedFile === "object" && selectedFile.url) {
        urlToOpen = selectedFile.url;
      }
      if (!urlToOpen) return;
      window.open(urlToOpen, "_blank", "noopener,noreferrer");
      if (revokeAfter) {
        const url = urlToOpen;
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      }
    },
    [selectedFile]
  );

  const acceptedMimes: string[] = [];
  let formatTags: string[];
  if (acceptOnlyImages) {
    acceptedMimes.push("image/png", "image/gif", "image/jpeg");
    formatTags = ["JPG", "PNG", "GIF"];
  } else if (acceptImages) {
    acceptedMimes.push("application/pdf", "image/png", "image/gif", "image/jpeg");
    formatTags = ["PDF", "JPG", "PNG", "GIF"];
  } else {
    acceptedMimes.push("application/pdf");
    formatTags = ["PDF"];
  }

  const dropTitle = heading || "Drop your file here";
  const dropSub = subheading || title || `Max 100 MB · ${formatTags.join(" / ")}`;

  const rawFileName = uploadFilename || selectedFile?.name || "Uploaded file";
  const displayName = truncate(rawFileName);

  const showIdle = uploadStatus === "idle" || uploadStatus === "dragging";
  const isUploading = uploadStatus === "uploading";
  const isPaused = uploadStatus === "paused";
  const isCancelled = uploadStatus === "cancelled";
  const isCompleted = uploadStatus === "completed";
  const isImageFile =
    (selectedFile instanceof File && selectedFile.type.startsWith("image/")) ||
    (typeof selectedFile === "object" && selectedFile?.type?.startsWith("image/"));

  return (
    <Wrapper style={style}>
      <Zone
        status={uploadStatus}
        disabled={disabled}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleZoneClick}
      >
        <HiddenInput
          ref={uploadRef}
          type="file"
          accept={acceptedMimes.join(", ")}
          onChange={handleInputChange}
          disabled={disabled}
        />

        {showIdle && (
          <IdleWrap>
            <DropIcon dragging={uploadStatus === "dragging"}>
              <CloudUploadOutlinedIcon sx={{ fontSize: 28 }} />
            </DropIcon>
            <DropTitle>{dropTitle}</DropTitle>
            <DropSub>{dropSub}</DropSub>
            <BrowseBtn
              type="button"
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                handleBrowseClick();
              }}
            >
              <FolderOpenOutlinedIcon sx={{ fontSize: 14 }} />
              Browse Files
            </BrowseBtn>
            {formatTags.length > 0 && (
              <FormatTags>
                {formatTags.map((t) => (
                  <FormatTag key={t}>{t}</FormatTag>
                ))}
              </FormatTags>
            )}
            {error && <ErrorBox>{error}</ErrorBox>}
          </IdleWrap>
        )}

        {!showIdle && (
          <>
            <UploadRow>
              <FileIcon status={uploadStatus}>
                {isCompleted && previewUrl && isImageFile ? (
                  <PreviewThumb src={previewUrl} alt={displayName} />
                ) : isCompleted ? (
                  <PictureAsPdfIcon sx={{ fontSize: 20 }} />
                ) : (
                  <CloudUploadOutlinedIcon sx={{ fontSize: 20 }} />
                )}
              </FileIcon>
              <UploadBody>
                <UploadTop>
                  {isCompleted ? (
                    <ClickableContainer
                      onClick={openFile}
                      title={rawFileName}
                      sx={{ maxWidth: "55%", minWidth: 0, alignItems: "center" }}
                    >
                      <Filename>{displayName}</Filename>
                    </ClickableContainer>
                  ) : (
                    <Filename title={rawFileName}>{displayName}</Filename>
                  )}
                  <UploadRight>
                    <StatusRow>
                      <StatusDot status={uploadStatus} />
                      <StatusLabel status={uploadStatus}>
                        {isPaused
                          ? "Paused"
                          : isCancelled
                          ? "Cancelled"
                          : isCompleted
                          ? "Uploaded"
                          : "Uploading"}
                      </StatusLabel>
                    </StatusRow>
                    <Pct status={uploadStatus}>{Math.round(progress)}%</Pct>
                  </UploadRight>
                </UploadTop>
                <Track>
                  <TrackFill status={uploadStatus} style={{ width: `${Math.round(progress)}%` }} />
                </Track>
                {(isUploading || isPaused) && (
                  <UploadMeta>
                    <MetaText>{speed}</MetaText>
                    <MetaText>{eta}</MetaText>
                  </UploadMeta>
                )}
                {isCompleted && (
                  <UploadMeta>
                    <MetaText>Ready to submit</MetaText>
                  </UploadMeta>
                )}
              </UploadBody>

              {(isUploading || isPaused) && !disabled && (
                <CtrlGroup>
                  <CtrlBtn
                    type="button"
                    variant={isPaused ? "resume" : "pause"}
                    onClick={togglePause}
                    aria-label={isPaused ? "Resume upload" : "Pause upload"}
                  >
                    {isPaused ? <PlayArrowIcon sx={{ fontSize: 18 }} /> : <PauseIcon sx={{ fontSize: 18 }} />}
                  </CtrlBtn>
                  <CtrlBtn type="button" variant="cancel" onClick={cancelUpload} aria-label="Cancel upload">
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </CtrlBtn>
                </CtrlGroup>
              )}

              {isCompleted && !disabled && (
                <CtrlGroup>
                  <CtrlBtn type="button" variant="pause" onClick={replaceCompleted} aria-label="Replace file">
                    <RefreshIcon sx={{ fontSize: 18 }} />
                  </CtrlBtn>
                  <CtrlBtn type="button" variant="cancel" onClick={removeCompleted} aria-label="Remove file">
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </CtrlBtn>
                </CtrlGroup>
              )}
            </UploadRow>

            {isCancelled && (
              <>
                {error && <ErrorBox>{error}</ErrorBox>}
                <CancelledActions>
                  <RetryBtn type="button" onClick={retryUpload}>
                    <RefreshIcon sx={{ fontSize: 14 }} />
                    Retry Upload
                  </RetryBtn>
                  <DismissBtn type="button" onClick={dismissCancel}>
                    Dismiss
                  </DismissBtn>
                </CancelledActions>
              </>
            )}

            {isCompleted && uploadButton && <UploadButtonSlot>{uploadButton}</UploadButtonSlot>}
          </>
        )}
      </Zone>

      {checkbox ? (
        <CheckboxWrap
          style={{
            pointerEvents: selectedFile ? "none" : "auto",
            opacity: selectedFile ? 0.5 : 1,
            cursor: selectedFile ? "not-allowed" : "auto",
          }}
        >
          <Field formAPI={form} name="deedCheck" label={label} type="checkbox" component={component} />
        </CheckboxWrap>
      ) : null}
    </Wrapper>
  );
};

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
`;

const iconPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
`;

const borderColor = (s: UploadStatus): string => {
  if (s === "dragging" || s === "paused") return "#BC92F8";
  if (s === "uploading") return "#2B3501";
  if (s === "cancelled") return "#E24B4A";
  if (s === "completed") return "#2B7A0B";
  return "#CCCCCC";
};

const backgroundFor = (s: UploadStatus): string => {
  if (s === "dragging") return "rgba(188,146,248,0.05)";
  if (s === "idle") return "#fff";
  // File-loaded states get a subtle ~8% darker neutral so the card stands out
  // without the dashed outline.
  return "rgba(0,0,0,0.08)";
};

const Wrapper = styled("div")`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 12px 0;

  @media (min-width: 900px) {
    margin: 30px 0 12px 0;
  }
`;

const Zone = styled("div", {
  shouldForwardProp: (p) => p !== "status" && p !== "disabled",
})<{ status: UploadStatus; disabled?: boolean }>`
  border: 2px
    ${({ status }) =>
      status === "idle" || status === "dragging" ? `dashed ${borderColor(status)}` : "solid transparent"};
  border-radius: 16px;
  background: ${({ status }) => backgroundFor(status)};
  padding: 24px 16px;

  @media (min-width: 768px) {
    padding: 40px 28px;
  }
  text-align: center;
  cursor: ${({ status, disabled }) => {
    if (disabled) return "not-allowed";
    return status === "idle" || status === "dragging" ? "pointer" : "default";
  }};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  transition: border-color 0.2s, background 0.2s;
  font-family: "Inter", sans-serif;
`;

const IdleWrap = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const DropIcon = styled("div", { shouldForwardProp: (p) => p !== "dragging" })<{
  dragging?: boolean;
}>`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${({ dragging }) => (dragging ? "rgba(188,146,248,0.12)" : "#F5F5F3")};
  border: 1.5px solid ${({ dragging }) => (dragging ? "#BC92F8" : "#E6E6E2")};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  color: ${({ dragging }) => (dragging ? "#BC92F8" : "#959A80")};
  transition: all 0.2s;
`;

const DropTitle = styled("div")`
  font-size: 16px;
  font-weight: 600;
  color: #1f1f1f;
  margin-bottom: 6px;
`;

const DropSub = styled("div")`
  font-size: 12px;
  color: #959a80;
  margin-bottom: 20px;
`;

const BrowseBtn = styled("button")`
  background: #f5f5f3;
  border: 1.5px solid #e6e6e2;
  color: #1f1f1f;
  border-radius: 50px;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 8px 20px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.13s;
  &:hover:not(:disabled) {
    border-color: #aaaaaa;
    background: #ededed;
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const FormatTags = styled("div")`
  display: flex;
  gap: 6px;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 14px;
`;

const FormatTag = styled("span")`
  font-family: "IBM Plex Mono", monospace;
  font-size: 10px;
  font-weight: 700;
  background: #f5f5f3;
  border: 1px solid #e6e6e2;
  color: #959a80;
  padding: 3px 8px;
  border-radius: 4px;
  letter-spacing: 0.05em;
`;

const ErrorBox = styled("div")`
  margin-top: 16px;
  background: #ffebee;
  color: #c62828;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 12px;
  font-weight: 600;
  max-width: 360px;
  word-break: break-word;
`;

const UploadRow = styled("div")`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 12px;
  min-width: 0;
  width: 100%;
`;

const fileIconBg = (s: UploadStatus): string => {
  if (s === "uploading") return "rgba(43,53,1,0.06)";
  if (s === "paused") return "rgba(188,146,248,0.1)";
  if (s === "cancelled") return "rgba(226,75,74,0.07)";
  if (s === "completed") return "rgba(43,122,11,0.08)";
  return "#F5F5F3";
};

const fileIconBorder = (s: UploadStatus): string => {
  if (s === "uploading") return "#2B3501";
  if (s === "paused") return "#BC92F8";
  if (s === "cancelled") return "#E24B4A";
  if (s === "completed") return "#2B7A0B";
  return "#E6E6E2";
};

const fileIconColor = (s: UploadStatus): string => {
  if (s === "uploading") return "#2B3501";
  if (s === "paused") return "#7B47CC";
  if (s === "cancelled") return "#A32D2D";
  if (s === "completed") return "#2B7A0B";
  return "#959A80";
};

const FileIcon = styled("div", { shouldForwardProp: (p) => p !== "status" })<{
  status: UploadStatus;
}>`
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: ${({ status }) => fileIconBg(status)};
  border: 1.5px solid ${({ status }) => fileIconBorder(status)};
  color: ${({ status }) => fileIconColor(status)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  transition: all 0.2s;
  animation: ${({ status }) => (status === "uploading" ? iconPulse : "none")} 1.4s ease-in-out infinite;
`;

const PreviewThumb = styled("img")`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const UploadBody = styled("div")`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  /* ensure body always takes full remaining row on mobile before CtrlGroup wraps */
  min-width: calc(100% - 54px);
  @media (min-width: 768px) {
    min-width: 0;
  }
`;

const UploadTop = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 7px;
  min-width: 0;
  gap: 8px;
`;

const Filename = styled("div")`
  font-size: 13px;
  font-weight: 600;
  color: #1f1f1f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  flex: 1;
  text-align: left;
`;

const UploadRight = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const StatusRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const dotColor = (s: UploadStatus): string => {
  if (s === "paused") return "#BC92F8";
  if (s === "cancelled") return "#E24B4A";
  if (s === "completed") return "#2B7A0B";
  return "#2B3501";
};

const StatusDot = styled("div", { shouldForwardProp: (p) => p !== "status" })<{
  status: UploadStatus;
}>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ status }) => dotColor(status)};
  flex-shrink: 0;
  transition: background 0.2s;
  animation: ${({ status }) => (status === "uploading" ? blink : "none")} 1.2s ease-in-out infinite;
`;

const labelColor = (s: UploadStatus): string => {
  if (s === "paused") return "#7B47CC";
  if (s === "cancelled") return "#A32D2D";
  return "#2B7A0B";
};

const StatusLabel = styled("span", { shouldForwardProp: (p) => p !== "status" })<{
  status: UploadStatus;
}>`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: ${({ status }) => labelColor(status)};
  transition: color 0.2s;
`;

const pctColor = (s: UploadStatus): string => {
  if (s === "paused") return "#7B47CC";
  if (s === "cancelled") return "#A32D2D";
  if (s === "completed") return "#2B7A0B";
  return "#1F1F1F";
};

const Pct = styled("div", { shouldForwardProp: (p) => p !== "status" })<{ status: UploadStatus }>`
  font-family: "IBM Plex Mono", monospace;
  font-size: 12px;
  font-weight: 700;
  color: ${({ status }) => pctColor(status)};
  transition: color 0.2s;
  min-width: 36px;
  text-align: right;
`;

const Track = styled("div")`
  flex: 1;
  height: 5px;
  background: #e6e6e2;
  border-radius: 10px;
  overflow: hidden;
`;

const fillColor = (s: UploadStatus): string => {
  if (s === "paused") return "#BC92F8";
  if (s === "cancelled") return "#E24B4A";
  if (s === "completed") return "#2B7A0B";
  return "#2B3501";
};

const TrackFill = styled("div", { shouldForwardProp: (p) => p !== "status" })<{
  status: UploadStatus;
}>`
  height: 100%;
  background: ${({ status }) => fillColor(status)};
  border-radius: 10px;
  transition: width 0.1s linear, background 0.3s;
`;

const UploadMeta = styled("div")`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 6px;
`;

const MetaText = styled("span")`
  font-family: "IBM Plex Mono", monospace;
  font-size: 10px;
  font-weight: 600;
  color: #aaaaaa;
`;

const CtrlGroup = styled("div")`
  display: flex;
  gap: 7px;
  flex-shrink: 0;
  /* on mobile wrap to its own row, right-aligned */
  width: 100%;
  justify-content: flex-end;
  @media (min-width: 768px) {
    width: auto;
    justify-content: flex-start;
  }
`;

const CtrlBtn = styled("button", { shouldForwardProp: (p) => p !== "variant" })<{
  variant: "pause" | "cancel" | "resume";
}>`
  width: 36px;
  height: 36px;
  border-radius: 9px;
  border: 1.5px solid ${({ variant }) => (variant === "resume" ? "rgba(188,146,248,0.5)" : "#E6E6E2")};
  background: ${({ variant }) => (variant === "resume" ? "rgba(188,146,248,0.08)" : "#fff")};
  color: ${({ variant }) => (variant === "resume" ? "#7B47CC" : "#1F1F1F")};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s;
  flex-shrink: 0;
  padding: 0;
  &:hover {
    ${({ variant }) =>
      variant === "pause"
        ? "border-color: #BC92F8; color: #7B47CC; background: rgba(188,146,248,0.08);"
        : ""}
    ${({ variant }) =>
      variant === "cancel"
        ? "border-color: #E24B4A; color: #A32D2D; background: rgba(226,75,74,0.06);"
        : ""}
    ${({ variant }) =>
      variant === "resume" ? "border-color: #7B47CC; background: rgba(188,146,248,0.16);" : ""}
  }
`;

const CancelledActions = styled("div")`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  justify-content: center;
`;

const RetryBtn = styled("button")`
  background: #2b3501;
  color: #fff;
  border: none;
  border-radius: 50px;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 8px 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: opacity 0.13s;
  &:hover {
    opacity: 0.85;
  }
`;

const DismissBtn = styled("button")`
  background: transparent;
  color: #959a80;
  border: 1.5px solid #e6e6e2;
  border-radius: 50px;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 8px 18px;
  cursor: pointer;
  transition: all 0.13s;
  &:hover {
    border-color: #999;
    color: #1f1f1f;
  }
`;

const UploadButtonSlot = styled("div")`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 20px;
`;

const CheckboxWrap = styled("div")`
  display: flex;
  justify-content: flex-start;
`;

const HiddenInput = styled("input")`
  display: none;
`;

export default DocUploadNew;
